import { DeviceMultiplier, DeviceUnitOfMeasure } from '../api/devices-api';
import { ConnectedMeterType, DeviceType, type Device } from '@/types/api';

// Shared option lists + read-side coercion helpers for the Pulse SALT
// transmitter's multiplier and unit-of-measure config. Used by both the
// Device Edit modal and bulk Setup Mode so the option labels and parsing
// stay in lock-step across surfaces.

export const SALT_MULTIPLIER_OPTIONS = [
  { value: String(DeviceMultiplier.OneToOne), label: '1:1' },
  { value: String(DeviceMultiplier.OneToTen), label: '1:10' },
  { value: String(DeviceMultiplier.OneToHundred), label: '1:100' },
  { value: String(DeviceMultiplier.OneToThousand), label: '1:1000' },
];

// Hardcoded fallback used until the SALT unit-types lookup table loads
// (or when the API is unreachable). The dropdown in the SALT device
// modal can prefer dynamic labels via buildSaltUomOptions() below, which
// merges API display names onto the canonical enum values so renames
// flow into the UI without breaking the existing wire format.
export const SALT_UOM_OPTIONS = [
  { value: String(DeviceUnitOfMeasure.Gallons), label: 'Gallons' },
  { value: String(DeviceUnitOfMeasure.Liters), label: 'Liters' },
  { value: String(DeviceUnitOfMeasure.CubicFeet), label: 'ft³' },
  { value: String(DeviceUnitOfMeasure.CubicMeters), label: 'm³' },
];

// Maps wire-code strings to the legacy enum values. Used to fold the
// API-returned SALT unit-types list back onto the four enum slots so the
// dropdown's selection mechanic continues to work while still picking up
// admin-edited display names. New custom unit types (beyond the four
// seeded entries) are ignored at this seam — they show in the admin
// page but aren't selectable until the device side drops the enum.
const WIRE_CODE_TO_ENUM: Record<string, DeviceUnitOfMeasure> = {
  gallons: DeviceUnitOfMeasure.Gallons,
  liters: DeviceUnitOfMeasure.Liters,
  cubic_feet: DeviceUnitOfMeasure.CubicFeet,
  cubic_meters: DeviceUnitOfMeasure.CubicMeters,
};

interface ApiUnitType { wireCode: string; displayName: string; isActive: boolean; }

export function buildSaltUomOptions(apiTypes: ApiUnitType[] | null): { value: string; label: string }[] {
  if (!apiTypes) return SALT_UOM_OPTIONS;
  const labelByEnum = new Map<DeviceUnitOfMeasure, string>();
  for (const t of apiTypes) {
    const slot = WIRE_CODE_TO_ENUM[t.wireCode];
    if (slot !== undefined && t.isActive) labelByEnum.set(slot, t.displayName);
  }
  // Preserve enum order so the dropdown's options stay stable.
  return SALT_UOM_OPTIONS.map((o) => ({
    value: o.value,
    label: labelByEnum.get(Number(o.value) as DeviceUnitOfMeasure) ?? o.label,
  }));
}

export function coerceMultiplier(value: unknown): DeviceMultiplier {
  const num = typeof value === 'string' ? Number(value) : value;
  if (num === DeviceMultiplier.OneToTen) return DeviceMultiplier.OneToTen;
  if (num === DeviceMultiplier.OneToHundred) return DeviceMultiplier.OneToHundred;
  if (num === DeviceMultiplier.OneToThousand) return DeviceMultiplier.OneToThousand;
  return DeviceMultiplier.OneToOne;
}

export function coerceUom(value: unknown): DeviceUnitOfMeasure {
  if (typeof value === 'string') {
    switch (value.toLowerCase()) {
      case 'liters':
        return DeviceUnitOfMeasure.Liters;
      case 'cubic_feet':
      case 'cubicfeet':
        return DeviceUnitOfMeasure.CubicFeet;
      case 'cubic_meters':
      case 'cubicmeters':
        return DeviceUnitOfMeasure.CubicMeters;
      default:
        return DeviceUnitOfMeasure.Gallons;
    }
  }
  if (typeof value === 'number') {
    if (value === 1) return DeviceUnitOfMeasure.Liters;
    if (value === 2) return DeviceUnitOfMeasure.CubicFeet;
    if (value === 3) return DeviceUnitOfMeasure.CubicMeters;
  }
  return DeviceUnitOfMeasure.Gallons;
}

// Pulse multiplier + UoM are exposed as structured top-level fields on
// the Device DTO (see api/Norgas.Api/DTOs/Devices/DeviceDto.cs). The
// previous implementation read them out of an opaque `config` JSON blob
// that was never actually populated on the wire — leaving the
// "Configure pulse encoder" banner stuck open even after a successful save.
export function readDeviceSaltConfig(device: Device): {
  multiplier: DeviceMultiplier;
  unitOfMeasure: DeviceUnitOfMeasure;
} {
  return {
    multiplier: coerceMultiplier(device.pulseMultiplier),
    unitOfMeasure: coerceUom(device.pulseUnitOfMeasure),
  };
}

// True when the device has both pulse-encoder fields populated. Used to
// gate the "Configure pulse encoder" CTA on the SALT detail page so the
// installer is nudged to set calibration before billing data flows.
export function hasSaltConfig(device: Device): boolean {
  return device.pulseMultiplier != null && device.pulseUnitOfMeasure != null;
}

// May 15 batch — resolve the effective pulse multiplier label for a
// device. When `customPulseMultiplier` is set, it wins and we render
// "1:N". Otherwise we fall back to the enum-based DeviceMultiplier
// (1:1 / 1:10 / 1:100 / 1:1000).
export function resolveEffectiveMultiplierLabel(device: Device): string {
  if (device.customPulseMultiplier != null && device.customPulseMultiplier > 0) {
    return `1:${device.customPulseMultiplier}`;
  }
  switch (device.pulseMultiplier) {
    case DeviceMultiplier.OneToOne: return '1:1';
    case DeviceMultiplier.OneToTen: return '1:10';
    case DeviceMultiplier.OneToHundred: return '1:100';
    case DeviceMultiplier.OneToThousand: return '1:1000';
    default: return '1:1';
  }
}

// Resolve the effective unit-of-measure label. Custom UoM wins;
// otherwise fall back to the SALT lookup (caller passes the list since
// it lives in a hook). When neither is available, return the standard
// "gal." suffix from the existing wire-format helper.
export function resolveEffectiveUnitOfMeasureLabel(
  device: Device,
  saltUnitTypes?: { wireCode: string; displayName: string }[] | null,
): string {
  if (device.customPulseUnitOfMeasure) return device.customPulseUnitOfMeasure;
  // Map enum value → wire-string → display via the SALT lookup.
  if (device.pulseUnitOfMeasure != null && saltUnitTypes) {
    const wireMap: Record<number, string> = {
      [DeviceUnitOfMeasure.Gallons]: 'gallons',
      [DeviceUnitOfMeasure.Liters]: 'liters',
      [DeviceUnitOfMeasure.CubicFeet]: 'cubic_feet',
      [DeviceUnitOfMeasure.CubicMeters]: 'cubic_meters',
    };
    const wireCode = wireMap[device.pulseUnitOfMeasure];
    const match = saltUnitTypes.find((t) => t.wireCode === wireCode);
    if (match) return match.displayName;
  }
  // Final fallback to the existing wire-format suffix helper.
  return 'gal.';
}

// Device Type column label for tables. Examples:
//   uMeter device                                 → "WaterMeter"
//   SALT, no connection set                       → "SALT"
//   SALT, ConnectedMeterType = Gas                → "SALT (Gas Meter)"
//   SALT, ConnectedMeterType = Other, custom set  → "SALT (Other: Pressure Sensor)"
//   SALT, ConnectedMeterType = Other, no custom   → "SALT (Other)"
export function deviceTypeLabel(device: Device): string {
  if (device.deviceType === DeviceType.WaterMeter) return 'WaterMeter';
  // SALT branch
  const conn = device.connectedMeterType;
  if (conn == null) return 'SALT';
  if (conn === ConnectedMeterType.Other) {
    const custom = device.connectedMeterTypeCustom?.trim();
    return custom ? `SALT (Other: ${custom})` : 'SALT (Other)';
  }
  return `SALT (${connectedMeterTypeDisplayName(conn)})`;
}

export function connectedMeterTypeDisplayName(value: ConnectedMeterType): string {
  switch (value) {
    case ConnectedMeterType.Gas: return 'Gas Meter';
    case ConnectedMeterType.Electric: return 'Electric Meter';
    case ConnectedMeterType.Water: return 'Water Meter';
    case ConnectedMeterType.Runtime: return 'Runtime';
    case ConnectedMeterType.Other: return 'Other';
  }
}

// Translates the API's wire-format unit-of-measure string into the short
// suffix used in card values, chart axes, and stat tiles.
//   "gallons"       → "gal."
//   "liters"        → "L"
//   "cubic_feet"    → "ft³"
//   "cubic_meters"  → "m³"
//   "pulse_format"  → "pulses"
//   "sensus_format" → "gal."  (Sensus-encoded SALT readings carry gallons × 100)
//   "undefined"     → "gal."  (decoder default fallback)
// Falls back to "gal." when unrecognised so a stray enum string never
// leaks into the UI as "179.57sensus_format" — display gets a sensible
// default and the underlying value is still inspectable in the raw column.
export function formatUnitOfMeasureSuffix(uom: string | undefined | null): string {
  if (!uom) return 'gal.';
  switch (uom.toLowerCase()) {
    case 'gallons':
    case 'sensus_format':
    case 'undefined':
      return 'gal.';
    case 'liters':
      return 'L';
    case 'cubic_feet':
    case 'cubicfeet':
      return 'ft³';
    case 'cubic_meters':
    case 'cubicmeters':
      return 'm³';
    case 'pulse_format':
    case 'pulses':
      return 'pulses';
    default:
      return 'gal.';
  }
}
